import { HORNAUT_KOPF_AVATAR } from "./hornaut-kopf";

/**
 * W104 A (AW-076): bei `kiAktiv` Rahmen/Kopf in KI-Violett (`ki-line`/`ki-soft`), Kopf-Avatar des Hornaut als Absender,
 * Unterzeile „KI-Einschätzung“; bei KI aus neutral wie zuvor (kein Violett, kein Kopf).
 *
 * KI-Karte (M-004 A4/B3, Design-Guide `ki-karte.md`): Kartenhülle für KI-Inhalte (Projekthornaut-Teaser,
 * Hornaut-Beobachtung und Wochenbericht). Eigenbau aus der Kartenhülle der `ListenKarte`; Kopf mit Sparkles-Symbol
 * (KI-Kennzeichnung, R-F), Titel und optionalem Stand.
 *
 * R-D (r22): die GANZE Karte trägt die Klasse `ai-gated` (Pflichtmarker, `scripts/ui-preline/erlaubte-fehlende-klassen.json`)
 * und zusätzlich `data-ki="gated"`. Auslöser in der Karte tragen `ai-gated ai-mark` (`Knopf ki`) und `data-ki="ausloeser"`
 * (beim Aufrufer). Ist die KI aus (`kiAktiv=false`, serverseitig aus Rolle ∧ Policy), steht oben der
 * `ausHinweis` (ein Satz, kein stilles Verschwinden) und der Aufrufer zeigt im Körper nur, was auch ohne KI sinnvoll ist
 * (z. B. den zuletzt freigegebenen Bericht); Auslöser fehlen dann.
 */
export function KiKarte({
  id,
  titel,
  stand,
  kiAktiv,
  ausHinweis,
  children,
  fuss,
}: {
  id: string;
  titel: string;
  /** Stand-Zeile rechts im Kopf („Stand heute · 14:30“). */
  stand?: string;
  kiAktiv: boolean;
  /** Satz, der bei ausgeschalteter KI oben im Körper steht. */
  ausHinweis: React.ReactNode;
  children: React.ReactNode;
  /** Fußbereich (z. B. Hinweis „Beobachtungen, keine Beschlüsse“ und Link). */
  fuss?: React.ReactNode;
}) {
  const titelId = `${id}-titel`;
  return (
    <>
      {/* Preline: dashboards/admin-dashboard/index.html#browsers-card (Hülle; Text und Aktionsleiste nach communication/communication-sub-chat-inbox/communication-cat-ai-chat-bubbles/long-form-ai-response-bubble-with-feedback-actions-and-sources-button) */}
      <section
        id={id}
        aria-labelledby={titelId}
        className={
          kiAktiv
            ? "ai-gated h-full flex flex-col bg-card border border-ki-line shadow-2xs rounded-xl"
            : "ai-gated h-full flex flex-col bg-card border border-card-line shadow-2xs rounded-xl"
        }
        data-ki="gated"
        data-ki-aktiv={kiAktiv ? "an" : "aus"}
      >
        <div
          className={
            kiAktiv
              ? "p-5 pb-3 flex flex-wrap justify-between items-center gap-2 bg-ki-soft border-b border-ki-line rounded-t-xl"
              : "p-5 pb-3 flex flex-wrap justify-between items-center gap-2"
          }
        >
          <div className="ms-1 flex items-center gap-x-2">
            {kiAktiv ? (
              /* Absender der Karte: Hornaut-Kopf mit Ring (CQ1/CQ3 a), dekorativ; Name steht als Text daneben */
              // eslint-disable-next-line @next/next/no-img-element -- statisches Kopf-Bild der Marke
              <img
                src={HORNAUT_KOPF_AVATAR}
                alt=""
                aria-hidden="true"
                width={32}
                height={32}
                className="shrink-0 size-8 rounded-full object-cover ring-1 ring-ki-line bg-ki-soft"
              />
            ) : (
              <span className="flex shrink-0 justify-center items-center size-7 bg-layer border border-layer-line text-muted-foreground-1 rounded-full" aria-hidden="true">
                {/* Sparkles = KI-Kennzeichnung (R-F), bei KI aus neutral */}
                <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  <path d="M20 3v4" />
                  <path d="M22 5h-4" />
                  <path d="M4 17v2" />
                  <path d="M5 18H3" />
                </svg>
              </span>
            )}
            <div>
              <h2 id={titelId} className="block text-lg font-semibold text-foreground leading-6">
                {titel}
              </h2>
              {kiAktiv && <p className="text-xs text-ki-text">KI-Einschätzung</p>}
            </div>
          </div>
          {stand && <p className="text-xs text-muted-foreground-1">{stand}</p>}
        </div>

        {/* KI an: Kopf ist ein abgesetztes Band, der Körper braucht eigenes Oberpolster (pt-4); KI aus: Kopf-pb-3 trägt den Abstand */}
        <div className={kiAktiv ? "px-5 pt-4 pb-5 grow space-y-3" : "px-5 pb-5 grow space-y-3"}>
          {kiAktiv ? null : (
            <p className="text-sm text-muted-foreground-1" role="status">
              {ausHinweis}
            </p>
          )}
          {children}
        </div>

        {fuss && <div className="px-5 py-3 border-t border-card-footer text-xs text-muted-foreground-1">{fuss}</div>}
      </section>
    </>
  );
}
